import Vue from "vue";
import { Store } from "vuex";
import { Action, Module, Mutation, VuexModule, getModule } from "vuex-module-decorators";

import { processCoreModelResponse } from "@/store/-utils";
import GiftGuidesApi from "@/store/gift-guides/-api";
import GiftGuidesPrivateApi from "@/store/gift-guides/-api-priv";
import {
  type GiftGuide,
  type GiftGuideFeed,
  type GiftGuideFeeds,
  type GiftGuideMap,
  type GiftGuideMeta,
  type GiftGuideProductsMap,
  type LocalizedCategoryMap,
  type ManyGiftGuidesResponse,
} from "@/store/gift-guides/types";
import ProductSetsApi from "@/store/product-sets/-api";
import mapData from "@/utils/transform/map-data";
import { fromV1, safeConvert, toV3 } from "@/utils/transform/ordered-uuid";

const emptyGiftGuideFactory: () => GiftGuideFeed = () => ({ ids: [], meta: null });

@Module({
  name: "gift-guides",
  stateFactory: true,
  namespaced: true,
})
export default class GiftGuidesStore extends VuexModule {
  giftGuides: GiftGuideMap = {};
  giftGuideProducts: GiftGuideProductsMap = {};
  feeds: GiftGuideFeeds = {};
  localizedCategories: LocalizedCategoryMap = {};

  get getFeedHasMorePages() {
    return (feedName: string) => {
      const feed = this.giftGuideFeed(feedName);
      return (feed.ids.length === 0 && feed.meta === null) || Boolean(feed.meta?.next);
    };
  }

  get hasFeedBeenFetched() {
    return (feedName: string) => {
      const feed = this.giftGuideFeed(feedName);
      return feed && feed.meta !== null && feed.ids.length > 0;
    };
  }

  get giftGuideFeed() {
    return (feedName: string) => this.feeds[feedName] ?? emptyGiftGuideFactory();
  }

  get haveCategoriesBeenFetched() {
    return "recipients" in this.localizedCategories;
  }

  get localizedAttributes() {
    return ({ audience: recipient, occasion }: { audience: string; occasion: string }) => {
      if (this.localizedCategories.occasions && this.localizedCategories.recipients) {
        return {
          recipient: this.localizedCategories.recipients[recipient]?.value,
          occasion: this.localizedCategories.occasions[occasion]?.value,
        };
      }
    };
  }

  @Action({ rawError: true })
  async fetchGiftGuide({ id }: { id: string }) {
    const api = new ProductSetsApi(this.store.$axios);
    const result = await api.getProductSetInfo({ id });

    await processCoreModelResponse(this.store, result.data);
  }

  @Action({ rawError: true })
  async fetchGiftGuidesByProfile({
    displayName,
    profileId,
    limit,
    overwrite = false,
  }: {
    displayName: string;
    profileId: string;
    limit?: number;
    overwrite?: boolean;
  }) {
    if (!profileId) {
      throw new Error(`No profile id found for display name ${displayName}`);
    }

    if (!this.getFeedHasMorePages(displayName) && overwrite === false) {
      return;
    }

    const api = new ProductSetsApi(this.store.$axios);
    const data: Parameters<(typeof api)["getProductSetsByProfile"]>[0] = { profileId, subtype: "GIFT_GUIDE", limit };
    const meta = this.feeds[displayName]?.meta || null;

    if (overwrite === false && meta?.next !== undefined) {
      data.next = meta.next;
    }

    const response = await api.getProductSetsByProfile(data);

    // Ensures only gift guides with translated categories are stored
    const { occasions, recipients } = this.localizedCategories;

    if (occasions && recipients) {
      const giftGuides = response.data.shopProductCollections.filter(
        (giftGuide) => giftGuide?.attributes && !!recipients[giftGuide.attributes.audience],
      );
      await processCoreModelResponse(this.store, { shopProductCollections: giftGuides, meta: response.data.meta });

      const ids = giftGuides?.map((item) => item.collectionId) ?? [];
      this.setGiftGuidesFeed({ feedName: displayName, ids, meta: response.data.meta, overwrite });
    }
  }

  @Action({ rawError: true })
  async fetchGiftGuidesByAudience({
    audience,
    limit,
    overwrite = false,
  }: {
    audience: string;
    limit?: number;
    overwrite?: boolean;
  }) {
    if (!this.getFeedHasMorePages(audience) && overwrite === false) {
      return;
    }

    const api = new GiftGuidesApi(this.store.$axios);
    const data: Parameters<(typeof api)["getGiftGuidesByRecipient"]>[0] = { audience, limit };
    const meta = this.feeds[audience]?.meta || null;

    if (overwrite === false && meta?.next !== undefined) {
      data.next = meta.next;
    }

    const result = await api.getGiftGuidesByRecipient(data);
    const giftGuides = result.data.shopProductCollections;

    if (giftGuides.length > 0) {
      await processCoreModelResponse(this.store, result.data);

      this.setGiftGuidesFeed({
        feedName: audience,
        ids: giftGuides.map((guide) => guide.collectionId),
        meta: result.data.meta || null,
        overwrite,
      });
    }
  }

  @Action({ rawError: true })
  async fetchFollowingFeedGiftGuides({ overwrite = false }) {
    const feedName = "following-gift-guides";
    const api = new GiftGuidesPrivateApi(this.store.$axios);

    const meta = this.feeds[feedName.toLowerCase()]?.meta || null;
    if (!this.getFeedHasMorePages(feedName) && overwrite === false) {
      return;
    }

    const response = await api.getFollowingGiftGuides(meta);
    if (response.data.groups.length > 0) {
      // Cast posts to product sets version of gift guides
      // TODO: Migrate product set gift guides to Post gift guides
      //  - Relies heavily on v3 posts being sufficiently supported
      const posts = response.data.posts ?? [];
      const coverPhotos = mapData("id", response.data.coverPhotos ?? []);
      const giftGuides = posts.map((post) => {
        const giftGuide: GiftGuide = {
          collectionId: safeConvert(post.id, fromV1, toV3),
          profileId: post.profileId,
          title: post.title ?? "",
          subtype: "GIFT_GUIDE",
          productCount: post.postProductCount,
          description: post.caption,
          coverPhotoUrls: post.coverPhotoIds.map((id) => coverPhotos[id].mediaCdnUrl),
          attributes: {
            audience: post.attributes?.audience?.toUpperCase() ?? "",
            occasion: post.attributes?.occasion?.toUpperCase() ?? "",
          },
        };
        return giftGuide;
      });

      // Set core models before feed lists
      await processCoreModelResponse(
        this.store,
        { profiles: response.data.profiles, shopProductCollections: giftGuides } as ManyGiftGuidesResponse,
        true,
      );
      const groupIds = response.data.groups.map(({ group }) => group);
      const groups = mapData("group", response.data.groups);
      response.data.profiles?.forEach((profile) => {
        if (!this.hasFeedBeenFetched(profile.displayName)) {
          const ids = groups[profile.id].postIds.map((id) => safeConvert(id, fromV1, toV3));
          this.setGiftGuidesFeed({ feedName: profile.displayName, ids });
        }
      });
      this.setGiftGuidesFeed({ feedName, ids: groupIds, meta: response.data.meta, overwrite });
    }
  }

  @Action({ rawError: true })
  async fetchGiftGuideProducts({ id, overwrite = false }: { id: string; overwrite?: boolean }) {
    if (!this.getFeedHasMorePages(id) && overwrite === false) {
      return;
    }

    const api = new ProductSetsApi(this.store.$axios);
    const data: Parameters<(typeof api)["getProductSetProducts"]>[0] = { id };
    const meta = this.feeds[id]?.meta || null;

    if (overwrite === false && meta?.next !== undefined) {
      data.next = meta.next;
    }

    const result = await api.getProductSetProducts(data);
    const giftGuideProducts = result.data.shopProducts;

    if (giftGuideProducts.length > 0) {
      await processCoreModelResponse(this.store, result.data);

      this.setGiftGuidesFeed({
        feedName: id,
        ids: giftGuideProducts.map((product) => product.shopProductId),
        meta: result.data.meta || null,
        overwrite,
      });
    }
  }

  @Action({ rawError: true })
  async fetchLocalizedCategories() {
    const api = new ProductSetsApi(this.store.$axios);
    // Backend wants the ISO Code and not just the language.
    const result = await api.getLocalizedCategories(this.store.$i18n.localeProperties.iso ?? "en");
    if (result.data?.recipients && result.data?.occasions) {
      await processCoreModelResponse(this.store, result.data);
    }
  }

  @Mutation
  setGiftGuide({ giftGuideId, giftGuide }: { giftGuideId: string; giftGuide: GiftGuide }) {
    Vue.set(this, "giftGuides", Object.assign({}, this.giftGuides, { [giftGuideId]: giftGuide }));
  }

  @Mutation
  setGiftGuides({ giftGuides }: { giftGuides: GiftGuideMap }) {
    Vue.set(this, "giftGuides", Object.assign({}, this.giftGuides, giftGuides));
  }

  @Mutation
  setGiftGuideProducts({ giftGuideProducts }: { giftGuideProducts: GiftGuideProductsMap }) {
    Vue.set(this, "giftGuideProducts", Object.assign({}, this.giftGuideProducts, giftGuideProducts));
  }

  @Mutation
  setLocalizedCategories(list: LocalizedCategoryMap) {
    Vue.set(this, "localizedCategories", list);
  }

  @Mutation
  setGiftGuidesFeed({
    feedName,
    ids,
    meta = null,
    overwrite = false,
  }: {
    feedName: string;
    ids: string[];
    meta?: GiftGuideMeta | null;
    overwrite?: boolean;
  }) {
    if (this.feeds[feedName] === undefined) {
      Vue.set(this.feeds, feedName, { ids: [], meta: null });
    }

    const start = overwrite ? 0 : this.feeds[feedName].ids.length;
    const length = overwrite ? this.feeds[feedName].ids.length : 0;
    this.feeds[feedName].ids.splice(start, length, ...ids);

    Vue.set(this.feeds[feedName], "meta", meta ?? null);
  }
}

export const getGiftGuidesStore = (store: Store<unknown>) => {
  return getModule(GiftGuidesStore, store);
};
